import {
  CommonActions,
  RouteProp,
  StackActions,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/native';
import ActivityIndicatorScreen from '../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../components/AlertBox';
import { H5 } from '../../../components/Typography';
import { useAssignStudyTemplate } from '../../../hooks/study-participant/useAssignStudyTemplate';
import { useLogout } from '../../../hooks/user/useLogout';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import { DrawerParamList } from '../../../navigation/DrawerNavigator';

// const redirectOptions = [
//   {
//     value: 'PreviousScreen',
//     label: 'Previous Screen - the screen proceeding this form'
//   },
//   { value: 'FormTemplate', label: 'Form Template' },
//   { value: 'MyChloe', label: 'My Chloe Dashboard' },
//   { value: 'StudiesNav', label: 'My Studies Dashboard' },
//   { value: 'StudyComingSoon', label: 'Study Coming Soon' },
//   { value: 'Intro', label: 'Home Page' }
// ];

const StyledViewContainer = styled.View`
  height: 100%;
`;

const ASSIGN_STUCK_MS = 30_000;

type AssignStudyTemplateProps<T> = FormElementProps<
  T,
  AssignStudyTemplateConfig
>;

export const AssignStudyTemplate = <T extends object>({
  element,
  stateNode
}: AssignStudyTemplateProps<T>) => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { studyTemplateId, redirectPath, redirectFormTemplateId } =
    element.config || {};
  const route = useRoute<RouteProp<DrawerParamList, 'FormTemplate'>>();
  const routeStudyParticipantId = route.params?.studyParticipantId;
  const { formValues, studyParticipantId, emitEvent } = useDynamicFormContext<
    { id?: number; studyParticipantId: number },
    any
  >();
  const { logout } = useLogout();
  const participantId =
    formValues?.studyParticipantId ??
    studyParticipantId ??
    routeStudyParticipantId;
  const {
    updatedStudyParticipant,
    assigningStudyTemplate,
    assigningStudyTemplateError,
    assignStudyTemplate,
    cancel: cancelAssignStudyTemplate
  } = useAssignStudyTemplate(participantId ?? 0, logout);
  const [stuckError, setStuckError] = useState<string | undefined>();
  const hasAssignedRef = useRef(false);

  useEffect(() => {
    // clean up on unmount
    return () => {
      cancelAssignStudyTemplate();
    };
  }, []);

  useEffect(() => {
    if (hasAssignedRef.current) return;
    if (!studyTemplateId || !participantId) return;
    if (!formValues?.id && !routeStudyParticipantId && !studyParticipantId) {
      return;
    }

    hasAssignedRef.current = true;
    setStuckError(undefined);
    // Capture stateNode.type at effect-fire time, before any await. The
    // state machine can transition during the in-flight assign, so a
    // post-await check would return false and 'final' would never emit
    // — leaving the form stuck even though the assign succeeded.
    const wasFinal = stateNode?.type === 'final';

    (async () => {
      try {
        await assignStudyTemplate(studyTemplateId);
        if (wasFinal) {
          await emitEvent('final', { transitions: [] });
        }
      } catch (err) {
        // Assign failed after retries (or was aborted on unmount); error
        // surfaces via assigningStudyTemplateError. Reset ref so the user
        // can retry on a subsequent dep change.
        // eslint-disable-next-line no-console
        console.warn('[AssignStudyTemplate] assign failed', err);
        hasAssignedRef.current = false;
      }
    })();
  }, [
    emitEvent,
    assignStudyTemplate,
    cancelAssignStudyTemplate,
    studyTemplateId,
    stateNode?.type,
    formValues?.id,
    studyParticipantId,
    routeStudyParticipantId,
    participantId
  ]);

  useEffect(() => {
    if (!studyTemplateId || !participantId) return;
    const timer = setTimeout(() => {
      if (
        !assigningStudyTemplate &&
        !updatedStudyParticipant &&
        !assigningStudyTemplateError &&
        !stuckError
      ) {
        setStuckError(
          'This step is taking longer than expected. Please go back and try again.'
        );
      }
    }, ASSIGN_STUCK_MS);
    return () => clearTimeout(timer);
  }, [
    studyTemplateId,
    participantId,
    assigningStudyTemplate,
    updatedStudyParticipant,
    assigningStudyTemplateError,
    stuckError
  ]);

  // redirect after assigning study template
  useEffect(() => {
    if (updatedStudyParticipant) {
      setStuckError(undefined);
      if (redirectPath !== 'PreviousScreen') {
        if (redirectPath === 'FormTemplate') {
          navigation.push('FormTemplate', {
            formTemplateId: redirectFormTemplateId
            // TODO: consider adding a configuration to:
            // Option 1: Start new form
            // Option 2: Load last edited form if exists
          });
          return;
        } else if (redirectPath === 'Study') {
          // Navigate to the Study Screen with back button to My Studies
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [
                {
                  name: 'DrawerNavigator',
                  state: {
                    index: 0,
                    routes: [
                      {
                        name: 'BottomTabNav',
                        state: {
                          index: 0,
                          routes: [
                            {
                              name: 'StudiesNav',
                              state: {
                                index: 1,
                                routes: [
                                  { name: 'MyStudies' },
                                  {
                                    name: 'StudyInfo',
                                    params: {
                                      studyId: updatedStudyParticipant.studyId,
                                      studyParticipantId:
                                        updatedStudyParticipant.id
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            })
          );
          return;
        } else if (redirectPath === 'MyData') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'MyDataNav'
              }
            })
          );
          return;
        } else if (redirectPath === 'StudiesNav') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'StudiesNav'
              }
            })
          );
          return;
        } else if (redirectPath === 'StudyComingSoon') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'StudiesNav',
                params: {
                  screen: 'StudyComingSoon'
                }
              }
            })
          );
          return;
        } else if (redirectPath === 'ToDoNav') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'ToDoNav'
              }
            })
          );
          return;
        }
      }
      // default to the previous screen prior to the FormTemplate
      const navigationState = navigation.getState();
      let popCount = 0;
      for (let i = navigationState.routes.length - 1; i >= 0; i--) {
        if (
          navigationState.routes[i].name !== 'StudyEventForm' &&
          navigationState.routes[i].name !== 'FormTemplate'
        ) {
          navigation.pop(popCount);
          return;
        }
        popCount++;
      }
      // else navigate to My Chloe
      navigation.dispatch(
        StackActions.replace('DrawerNavigator', {
          screen: 'BottomTabNav',
          params: {
            screen: 'HomeNav'
          }
        })
      );
    }
  }, [
    navigation,
    redirectPath,
    redirectFormTemplateId,
    updatedStudyParticipant
  ]);
  const displayError =
    stuckError || assigningStudyTemplateError?.message;
  const showSpinner =
    assigningStudyTemplate ||
    (!!updatedStudyParticipant && !displayError) ||
    (!!studyTemplateId &&
      !!participantId &&
      !formValues?.id &&
      !routeStudyParticipantId &&
      !studyParticipantId);

  return (
    <StyledViewContainer>
      {showSpinner ? (
        <ActivityIndicatorScreen backgroundColor='transparent' />
      ) : displayError ? (
        <AlertBox severity='error'>{displayError}</AlertBox>
      ) : (
        <AlertBox severity='error'>
          Attempting to assign a study template. However, no study template was
          specified. Please contact the study administrator.
        </AlertBox>
      )}
    </StyledViewContainer>
  );
};

interface AssignStudyTemplateConfig {
  studyTemplateId?: number;
  redirectPath?: string;
  redirectFormTemplateId?: number;
}

export const AssignStudyTemplatePlugin: FormElementPlugin<
  any,
  AssignStudyTemplateConfig
> = {
  name: 'Assign Study Template',
  type: 'AssignStudyTemplate',
  element: AssignStudyTemplate as React.FC<
    FormElementProps<any, AssignStudyTemplateConfig>
  >
};
